/**
 * Wire-контракт цен и остатков (`business-data-v1.schema.json`) и его проверка
 * на границе. Деньги и количества — JSON-числа (decimal на сервере).
 */
import {
  asBoolean,
  asNumber,
  asOneOf,
  asObject,
  asString,
  field,
  mapArray,
  optional,
  type Raw,
} from './decode'
import type {
  PriceComparisonFilter,
  PriceByType,
  ProductPriceRow,
  ProductStockRow,
  RetailPriceDiscrepancy,
  WarehouseStock,
} from '../model/types'
import type { PriceList } from '../model/priceList'
import type { PriceType, PriceTypeList } from '../model/priceTypes'

const text = (raw: Raw, name: string, path: string) => asString(field(raw, name, path), `${path}.${name}`)
const num = (raw: Raw, name: string, path: string) => asNumber(field(raw, name, path), `${path}.${name}`)

export function decodeWarehouseStock(raw: unknown, path: string): WarehouseStock {
  const item = asObject(raw, path)
  return {
    warehouseRefKey: text(item, 'warehouseRefKey', path),
    warehouseName: text(item, 'warehouseName', path),
    quantity: num(item, 'quantity', path),
  }
}

export const decodeWarehouseStocks = (raw: unknown, path: string) => mapArray(raw, path, decodeWarehouseStock)

export function decodeProductStockRow(raw: unknown, path: string): ProductStockRow {
  const item = asObject(raw, path)
  return {
    productId: text(item, 'productId', path),
    warehouseRefKey: text(item, 'warehouseRefKey', path),
    quantity: num(item, 'quantity', path),
  }
}

export const decodeProductStockRows = (raw: unknown, path: string) => mapArray(raw, path, decodeProductStockRow)

export function decodeProductPriceRow(raw: unknown, path: string): ProductPriceRow {
  const item = asObject(raw, path)
  return {
    productId: text(item, 'productId', path),
    priceTypeRefKey: text(item, 'priceTypeRefKey', path),
    price: num(item, 'price', path),
  }
}

export const decodeProductPriceRows = (raw: unknown, path: string) => mapArray(raw, path, decodeProductPriceRow)

/** Цены одного товара: объект «вид цен → цена» превращается в упорядоченный список. */
export function decodePriceMap(raw: unknown, path: string): readonly PriceByType[] {
  const map = asObject(raw, path)
  return Object.entries(map)
    .map(([priceTypeRefKey, price]) => ({ priceTypeRefKey, price: asNumber(price, `${path}.${priceTypeRefKey}`) }))
    .sort((a, b) => a.priceTypeRefKey.localeCompare(b.priceTypeRefKey))
}

export function decodeDiscrepancy(raw: unknown, path: string): RetailPriceDiscrepancy {
  const item = asObject(raw, path)
  return {
    productId: text(item, 'productId', path),
    name: text(item, 'name', path),
    sku: optional(item.sku, `${path}.sku`, asString),
    prices: mapArray(field(item, 'prices', path), `${path}.prices`, (point, pointPath) => {
      const entry = asObject(point, pointPath)
      return { priceTypeKey: text(entry, 'priceTypeKey', pointPath), price: num(entry, 'price', pointPath) }
    }),
  }
}

export const decodeDiscrepancies = (raw: unknown, path: string) => mapArray(raw, path, decodeDiscrepancy)

export function decodeFilter(raw: unknown, path: string): PriceComparisonFilter {
  const item = asObject(raw, path)
  return { first: text(item, 'first', path), second: text(item, 'second', path), updatedAt: text(item, 'updatedAt', path) }
}

/** Итог записи или удаления цены: `applied` — 1С приняла; `unchanged` — в 1С уже так. */
export function decodePriceRecordOutcome(raw: unknown, path: string): 'applied' | 'unchanged' {
  const data = asObject(raw, path)
  return asOneOf(field(data, 'outcome', path), `${path}.outcome`, ['applied', 'unchanged'] as const)
}

/** Вид цен базы (RP-FB39): справочник «Виды цен». */
export function decodePriceType(raw: unknown, path: string): PriceType {
  const type = asObject(raw, path)
  const percent = optional(type.percent, `${path}.percent`, asNumber)
  return {
    key: text(type, 'key', path),
    name: text(type, 'name', path),
    code: optional(type.code, `${path}.code`, asString),
    predefined: asBoolean(field(type, 'predefined', path), `${path}.predefined`),
    includesVat: asBoolean(field(type, 'includesVat', path), `${path}.includesVat`),
    baseTypeKey: optional(type.baseTypeKey, `${path}.baseTypeKey`, asString),
    baseTypeName: optional(type.baseTypeName, `${path}.baseTypeName`, asString),
    percent,
    comment: optional(type.comment, `${path}.comment`, asString),
    kind: optional(type.kind, `${path}.kind`, asString),
    inactive: asBoolean(field(type, 'inactive', path), `${path}.inactive`),
    deletionMark: asBoolean(field(type, 'deletionMark', path), `${path}.deletionMark`),
  }
}

export function decodePriceTypeList(raw: unknown, path: string): PriceTypeList {
  const list = asObject(raw, path)
  return { items: mapArray(field(list, 'items', path), `${path}.items`, decodePriceType) }
}

/** Прайс по виду цен (RP-FB10): срез, виды цен и страница строк. */
export function decodePriceList(raw: unknown, path: string): PriceList {
  const list = asObject(raw, path)
  const slice = asObject(field(list, 'slice', path), `${path}.slice`)
  const slicePath = `${path}.slice`
  return {
    slice: {
      observedAt: text(slice, 'observedAt', slicePath),
      freshness: asOneOf(field(slice, 'freshness', slicePath), `${slicePath}.freshness`, ['fresh', 'stale'] as const),
      staleReason: optional(slice.staleReason, `${slicePath}.staleReason`, asString),
      refreshing: asBoolean(field(slice, 'refreshing', slicePath), `${slicePath}.refreshing`),
    },
    priceTypes: mapArray(field(list, 'priceTypes', path), `${path}.priceTypes`, (item, itemPath) => {
      const type = asObject(item, itemPath)
      return { key: text(type, 'key', itemPath), name: optional(type.name, `${itemPath}.name`, asString) }
    }),
    priceType: optional(list.priceType, `${path}.priceType`, asString),
    total: num(list, 'total', path),
    page: num(list, 'page', path),
    pageSize: num(list, 'pageSize', path),
    items: mapArray(field(list, 'items', path), `${path}.items`, (item, itemPath) => {
      const row = asObject(item, itemPath)
      return {
        productKey: text(row, 'productKey', itemPath),
        name: optional(row.name, `${itemPath}.name`, asString),
        sku: optional(row.sku, `${itemPath}.sku`, asString),
        price: num(row, 'price', itemPath),
        period: text(row, 'period', itemPath),
        priceTypeKey: text(row, 'priceTypeKey', itemPath),
        priceTypeName: optional(row.priceTypeName, `${itemPath}.priceTypeName`, asString),
      }
    }),
  }
}
